import { Button, Flex, HStack, Icon, Spacer, VStack } from "@chakra-ui/react";
import { RadioCard, RadioCardGroup } from "components/FormElements/RadioCardGroup";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { API_ROUTES } from "definitions/constants/routeConstants";
import { OnboardingJourneyNetworkRelation } from "models/v2/onboardingJourneyNetworkRelation";
import { titleize } from "helpers/string";
import { FloatingField } from "design-system";
import { Text14 } from "components/Text";
import { Schema, schema } from "./schema";
import { SelectEmailTargetValue } from "./models";
import {
  CUSTOM_EMAIL_FIELD,
  EMAIL_TYPE_FIELD,
  RELATION_FIELD,
  SELECTABLE_EMAIL_TARGET_TYPE,
  USER_FIELD,
} from "./fields";

function EmailTargetType({ name, icon }: { name: string; icon: any }) {
  return (
    <Flex direction="column" gap="2px" justify="center" alignItems="stretch" height="100%">
      <Text14 textAlign="center" fontWeight="600" color="fg.muted">
        {name}
      </Text14>
      <VStack alignItems="center" gap="0">
        <Icon as={icon} boxSize="6" color="fg.subtle" />
      </VStack>
    </Flex>
  );
}

export default function NewRecipientForm({
  includeRelationTarget,
  onSubmit,
}: {
  includeRelationTarget: boolean;
  onSubmit: (data: Schema) => void;
}) {
  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    watch,
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: { emailTargetType: includeRelationTarget ? "relation" : "user", emailType: "work_email" },
  });

  const selectableTypes = useMemo(
    () =>
      includeRelationTarget
        ? SELECTABLE_EMAIL_TARGET_TYPE
        : SELECTABLE_EMAIL_TARGET_TYPE.filter((targetType) => targetType.value !== "relation"),
    [includeRelationTarget],
  );

  const emailTargetType = watch("emailTargetType");

  const handleFormSubmit = async (data: Schema) => {
    try {
      // Transform data to only include relevant fields for backend
      // Use camelCase - the CaseConverter middleware will convert to snake_case automatically
      const payload: any = {};

      if (data.emailTargetType === "user") {
        payload.userId = data.userId;
        payload.emailType = data.emailType;
      } else if (data.emailTargetType === "relation") {
        payload.onboardingJourneyNetworkRelationId = data.onboardingJourneyNetworkRelationId;
        payload.emailType = data.emailType;
      } else if (data.emailTargetType === "custom") {
        payload.customEmail = data.customEmail;
      }

      onSubmit(payload);

      // Reset form fields
      setValue("userId", "");
      setValue("onboardingJourneyNetworkRelationId", "");
      setValue("customEmail", "");
      setValue("emailType", "work_email");
    } catch (e) {
      console.error("Submit failed", e);
      // Optional: show error UI here
    }
  };

  return (
    <>
      <RadioCardGroup<SelectEmailTargetValue>
        direction="row"
        justify="stretch"
        value={emailTargetType}
        spacing={3}
        onChange={(value) => setValue("emailTargetType", value)}
        flexWrap="wrap"
        py={4}
      >
        {selectableTypes.map((field) => (
          <RadioCard
            key={field.value}
            value={field.value}
            alignSelf="stretch"
            minW="120px"
            maxW="120px"
            minH="100px"
            maxH="100px"
          >
            <EmailTargetType {...field} />
          </RadioCard>
        ))}
      </RadioCardGroup>
      {emailTargetType === "user" && (
        <FloatingField name={USER_FIELD.name} errors={errors[USER_FIELD.name]?.message}>
          <FloatingField.Label>{USER_FIELD.label}</FloatingField.Label>
          <FloatingField.UserSelect control={control} name={USER_FIELD.name} />
        </FloatingField>
      )}
      {emailTargetType === "relation" && (
        <FloatingField name={RELATION_FIELD.name} errors={errors[RELATION_FIELD.name]?.message}>
          <FloatingField.Label>{RELATION_FIELD.label}</FloatingField.Label>
          <FloatingField.SingleSelect<OnboardingJourneyNetworkRelation>
            fetchPath={API_ROUTES.onboardingJourneyNetworkRelations.base}
            extractValue={(v: OnboardingJourneyNetworkRelation) => v.id}
            embedOption={(v: OnboardingJourneyNetworkRelation) => ({
              value: v.id,
              label: `${titleize(v.name)}`,
              rawValue: v,
            })}
            control={control}
            name={RELATION_FIELD.name}
          />
        </FloatingField>
      )}
      {emailTargetType !== "custom" && (
        <FloatingField name={EMAIL_TYPE_FIELD.name} errors={errors[EMAIL_TYPE_FIELD.name]?.message}>
          <FloatingField.Label>{EMAIL_TYPE_FIELD.label}</FloatingField.Label>
          <FloatingField.StaticSelect
            control={control}
            name={EMAIL_TYPE_FIELD.name}
            isClearable={false}
            options={[
              { label: "Work", value: "work_email" },
              { label: "Personal", value: "personal_email" },
            ]}
          />
        </FloatingField>
      )}
      {emailTargetType === "custom" && (
        <FloatingField name={CUSTOM_EMAIL_FIELD.name} errors={errors.customEmail?.message}>
          <FloatingField.Label>{CUSTOM_EMAIL_FIELD.label}</FloatingField.Label>
          <FloatingField.Text control={control} name={CUSTOM_EMAIL_FIELD.name} type="email" />
        </FloatingField>
      )}
      <HStack>
        <Spacer />
        <Button onClick={handleSubmit(handleFormSubmit)}>Add</Button>
      </HStack>
    </>
  );
}
